import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api, { apiErr } from "@/lib/api";
import { fmtDate } from "@/components/DateInput";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FolderOpen, Plus, Search, Download, Trash2, Eye, FileText, FileImage, FileSpreadsheet, File as FileIcon, Loader2, X } from "lucide-react";

const CATEGORIES = [
  { k: "umum", l: "Umum" }, { k: "pajak", l: "Pajak" }, { k: "legal", l: "Legal" },
  { k: "keuangan", l: "Keuangan" }, { k: "kontrak", l: "Kontrak" }, { k: "lainnya", l: "Lainnya" },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.k, c.l]));

const fmtSize = (n) => {
  const b = Number(n || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const isImage = (d) => (d.content_type || "").startsWith("image/");
const isPdf = (d) => (d.content_type || "").includes("pdf");
const typeIcon = (d) => (isImage(d) ? FileImage : isPdf(d) ? FileText : (d.ext === "xlsx" || d.ext === "xls" || d.ext === "csv") ? FileSpreadsheet : FileIcon);

export default function Documents() {
  const { t } = useI18n();
  const { selectedClientId, clients } = useAuth();
  const clientId = selectedClientId;
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [loading, setLoading] = useState(false);
  const [thumbs, setThumbs] = useState({});
  const [preview, setPreview] = useState(null); // { doc, url }
  const clientName = clients.find((c) => c.id === clientId)?.name || "";

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const p = new URLSearchParams({ client_id: clientId });
      if (cat !== "all") p.set("category", cat);
      const { data } = await api.get(`/documents?${p.toString()}`);
      setDocs(data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [clientId, cat]);
  useEffect(() => { load(); }, [load]);

  // lazy-load image thumbnails as blobs
  useEffect(() => {
    docs.filter(isImage).forEach(async (d) => {
      if (thumbs[d.id]) return;
      try {
        const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
        setThumbs((s) => ({ ...s, [d.id]: URL.createObjectURL(res.data) }));
      } catch (e) { console.debug("thumbnail load failed", d.id, e); }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs]);
  useEffect(() => () => Object.values(thumbs).forEach((u) => URL.revokeObjectURL(u)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const openPreview = async (d) => {
    if (isImage(d) || isPdf(d)) {
      try {
        const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
        setPreview({ doc: d, url: URL.createObjectURL(res.data) });
      } catch (e) { toast.error("Gagal memuat pratinjau"); }
    } else {
      downloadDoc(d);
    }
  };
  const closePreview = () => { if (preview?.url) URL.revokeObjectURL(preview.url); setPreview(null); };

  const downloadDoc = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download?dl=1`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = d.original_filename || "file"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Gagal mengunduh file"); }
  };

  const del = async (d) => {
    if (!window.confirm(`Hapus file "${d.label}"?`)) return;
    try { await api.delete(`/documents/${d.id}`); toast.success("File dihapus"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const filtered = docs.filter((d) => !q || `${d.label} ${d.original_filename}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="documents-page">
      <div className="page-sticky flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight flex items-center gap-2"><FolderOpen className="h-6 w-6 text-primary" /> {t("nav.documents")}</h1>
          <p className="text-sm text-muted-foreground mt-1">Pustaka dokumen per klien — gambar, PDF, dan dokumen umum (maks 10 MB). {clientName && `· ${clientName}`}</p>
        </div>
        <UploadForm clientId={clientId} onDone={load} />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Cari nama file / label..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9 w-64" data-testid="doc-search" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-44 h-9" data-testid="doc-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kategori</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c.k} value={c.k}>{c.l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading && <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Memuat...</div>}
      {!loading && filtered.length === 0 && (
        <Card className="p-12 flex flex-col items-center justify-center text-muted-foreground text-sm gap-2" data-testid="doc-empty">
          <FolderOpen className="h-8 w-8" /> Belum ada dokumen. Klik "Unggah Dokumen" untuk menambah.
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
        {filtered.map((d) => {
          const Icon = typeIcon(d);
          return (
            <Card key={d.id} className="overflow-hidden group" data-testid={`doc-card-${d.id}`}>
              <button className="h-28 w-full bg-secondary/40 flex items-center justify-center overflow-hidden" onClick={() => openPreview(d)} data-testid={`doc-preview-${d.id}`} title="Pratinjau">
                {isImage(d) && thumbs[d.id]
                  ? <img src={thumbs[d.id]} alt={d.label} className="max-h-full max-w-full object-cover w-full h-full" />
                  : <Icon className="h-10 w-10 text-muted-foreground" />}
              </button>
              <div className="p-2.5 space-y-1.5">
                <div className="flex items-start justify-between gap-1">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate" title={d.label}>{d.label}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{d.original_filename} · {fmtSize(d.size)}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="text-[9px]">{CAT_LABEL[d.category] || d.category}</Badge>
                  <div className="flex gap-0.5">
                    <Button variant="ghost" size="icon" className="h-7 w-7" title="Unduh" onClick={() => downloadDoc(d)} data-testid={`doc-download-${d.id}`}><Download className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" title="Hapus" onClick={() => del(d)} data-testid={`doc-delete-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                <div className="text-[10px] text-muted-foreground">{d.uploaded_by} · {fmtDate(d.created_at)}</div>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="truncate pr-8">{preview?.doc.label}</DialogTitle>
            <DialogDescription className="sr-only">Pratinjau dokumen {preview?.doc.label}</DialogDescription>
          </DialogHeader>
          {preview && isPdf(preview.doc) && <iframe src={preview.url} title={preview.doc.label} className="w-full h-[70vh] rounded-md border border-border" data-testid="doc-preview-frame" />}
          {preview && isImage(preview.doc) && <img src={preview.url} alt={preview.doc.label} className="max-w-full max-h-[70vh] mx-auto object-contain" data-testid="doc-preview-img" />}
          {preview && (
            <DialogFooter>
              <Button variant="outline" onClick={() => { downloadDoc(preview.doc); }} data-testid="doc-preview-download"><Download className="h-4 w-4 mr-2" />Unduh</Button>
              <Button onClick={closePreview}>Tutup</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UploadForm({ clientId, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ label: "", category: "umum", description: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const save = async () => {
    if (!file) { toast.error("Pilih file terlebih dahulu"); return; }
    setSaving(true);
    const fd = new FormData();
    fd.append("client_id", clientId);
    fd.append("file", file);
    fd.append("label", f.label);
    fd.append("category", f.category);
    fd.append("description", f.description);
    try {
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dokumen diunggah");
      setOpen(false); setF({ label: "", category: "umum", description: "" }); setFile(null);
      onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setFile(null); setF({ label: "", category: "umum", description: "" }); } }}>
      <DialogTrigger asChild><Button data-testid="doc-upload-btn"><Plus className="h-4 w-4 mr-2" />Unggah Dokumen</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Unggah Dokumen</DialogTitle>
          <DialogDescription className="sr-only">Formulir unggah dokumen klien</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>File (gambar / PDF / dokumen, maks 10 MB)</Label>
            <div className="flex items-center gap-2">
              <Input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt" className="h-9"
                onChange={(e) => { const fl = e.target.files?.[0] || null; setFile(fl); if (fl && !f.label) setF((s) => ({ ...s, label: fl.name })); }} data-testid="doc-upload-file" />
              {file && <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}><X className="h-4 w-4" /></Button>}
            </div>
            {file && <p className="text-xs text-muted-foreground">{file.name} · {fmtSize(file.size)}</p>}
          </div>
          <div className="space-y-1.5"><Label>Label</Label><Input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="mis. Akta Pendirian 2023" data-testid="doc-label" /></div>
          <div className="space-y-1.5"><Label>Kategori</Label>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger data-testid="doc-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.k} value={c.k}>{c.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Catatan (opsional)</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="doc-desc" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving || !file} data-testid="doc-save">{saving ? "Mengunggah..." : "Unggah"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
